import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateCaso } from '@/hooks/useCasos';
import { Caso } from '@/types/casos';
import { GradientSelector } from './GradientSelector';
import { IconSelector } from './IconSelector';

interface EditCasoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caso: Caso;
}

export function EditCasoDialog({ open, onOpenChange, caso }: EditCasoDialogProps) {
  const [title, setTitle] = useState(caso.title);
  const [description, setDescription] = useState(caso.description || '');
  const [coverGradient, setCoverGradient] = useState(caso.cover_gradient);
  const [icon, setIcon] = useState(caso.icon || 'scale');
  const updateCaso = useUpdateCaso();

  useEffect(() => {
    if (open) {
      setTitle(caso.title);
      setDescription(caso.description || '');
      setCoverGradient(caso.cover_gradient);
      setIcon(caso.icon || 'scale');
    }
  }, [open, caso]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      return;
    }

    updateCaso.mutate(
      {
        id: caso.id,
        title: title.trim(),
        description: description.trim() || undefined,
        cover_gradient: coverGradient,
        icon,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={onOpenChange}
      modal={true}
    >
      <DialogContent 
        className="sm:max-w-[500px]"
        onPointerDownOutside={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onInteractOutside={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Editar Caso</DialogTitle>
        </DialogHeader>
        
        <form 
          onSubmit={handleSubmit} 
          className="space-y-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="space-y-2">
            <Label htmlFor="edit-title">Nome do Caso *</Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Ação de Indenização"
              required
              minLength={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">Descrição (opcional)</Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breve descrição do caso..."
              rows={4}
            />
          </div>

          <GradientSelector 
            value={coverGradient}
            onChange={setCoverGradient}
          />

          <IconSelector 
            value={icon}
            onChange={setIcon}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateCaso.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={updateCaso.isPending || !title.trim()}>
              {updateCaso.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
